import React from 'react';
import ArriveGroup from './ArriveGroup';
import SongTabs from '../notes/SongTabs';
import { todaySongIds } from '../notes/todaySongs';
import LessonWarmups from '../warmups/LessonWarmups';
import NoteList from '../notes/NoteList';
import { WARMUPS_SONG_ID, carryForwardForSong } from '../../../../services/songNotesModel';
import './NotesColumn.css';

/**
 * The Focus spine's scroll region: song tabs → the Arrive group (Last lesson,
 * Carry-forward, From home) → tool row → note list. The jot bar is NOT here —
 * it pins to the viewport bottom (index.js owns it) so it stays in the thumb
 * zone while this column scrolls.
 *
 * TWO CONTROLS LEFT THIS COLUMN IN DESIGN PASS 2 BATCH B (2026-09-21). The
 * tool row's Recap button (change 7): the topbar Recap is on every tab at
 * every width and the Last lesson row opens the same drawer at its history,
 * so a third route on song tabs only was clutter, and recapEntryPoints.test.js
 * pins that this file no longer knows how to open it. The "Wrap up lesson"
 * ghost button at the foot of the list (change 10): Complete lesson now lives
 * in the Recap drawer's footer, two taps from any tab (Recap, then Complete),
 * next to the writing it completes.
 *
 * THE ARRIVE GROUP LIVES HERE AT EVERY WIDTH (design pass 4 P4.3,
 * 2026-10-04). Last lesson, Carry-forward and From home used to render in the
 * context rail and come down here only when the rail was hidden; the rail is
 * now the Private file, so this is their one mount, painted at every width
 * (`.ld-notescol__context` has no media condition). layoutContextRule.test.js
 * pins that.
 *
 * First lesson: no carry-forward, only the Warm-ups tab, and a dashed empty
 * card offering the two honest actions — find her song, or just start jotting.
 * The jot bar is never disabled, so a song-less note simply lands on Warm-ups.
 */
const NotesColumn = ({
	student,
	carryForward,
	board,
	notes,
	teacherNames,
	currentTeacherId,
	onDeliver,
	onAddSong,
	onOpenPanel = null, // pass 3 D2: (mode) => opens the Song panel sheet (<1200)
	panelCounts = {},
	warmups = null,
	deckInStrip = false,
	previousRecaps = null,
	onOpenPreviousRecaps = null,
	sections = [], // the current song's sections (2026-09-25)
	carryOpen = true,
	onToggleCarry = null,
	canManageNotes = false, // admin / owner: edit + delete Song Notes (2026-09-27)
	arrive = {}, // pass 3: { onCarryAct, carryError, onDismissCarryError, familyQuestions }
	lessonId = null,
	setupRow = null, // pass 4 S6.1: the song being set up, below 1200
	songTabs = {}, // { opened, closed, open(id), close(id) }: which songs are pills this lesson
}) => {
	const isFirstLesson =
		board.songs.length === 1 &&
		board.songs[0].id === WARMUPS_SONG_ID &&
		board.songs.every((s) => (s.notes || []).length === 0);

	const currentIsWarmups = board.currentSong?.id === WARMUPS_SONG_ID;
	// 2026-10-02: the pills are what this lesson is using; the rest of the
	// repertoire is behind the Songs picker (notes/todaySongs.js).
	const today = todaySongIds({
		songs: board.songs,
		lessonId,
		currentSongId: board.currentSongId,
		opened: songTabs.opened || [],
		closed: songTabs.closed || [],
	});
	const pillSongs = board.songs.filter((s) => today.has(s.id));

	return (
		<div className="ld-notescol">
			{/* From home (the family's questions) no longer sits here above the
			    tabs: pass 3 C2 moved it into the Arrive group below, with Last
			    lesson and Carry-forward, so it lives wherever they do. */}
			<SongTabs
				songs={pillSongs}
				allSongs={board.songs}
				today={today}
				currentSongId={board.currentSongId}
				onSelect={board.setCurrentSongId}
				onOpen={(id) => {
					if (songTabs.open) songTabs.open(id);
					board.setCurrentSongId(id);
				}}
				onAdd={onAddSong}
				onRemove={(id) => {
					if (songTabs.close) songTabs.close(id);
					// Tucking away the song on screen goes back to Warm-ups.
					if (id === board.currentSongId) board.setCurrentSongId(WARMUPS_SONG_ID);
				}}
			/>
			{setupRow}

			{/* The Arrive group, under the tab row, at every width (pass 4 P4.3). */}
			{student && (
				<div className="ld-notescol__context">
					<ArriveGroup
						previousRecaps={previousRecaps}
						onOpenPreviousRecaps={onOpenPreviousRecaps}
						carryForward={carryForwardForSong(carryForward, board.currentSong?.id)}
						carryOpen={carryOpen}
						onToggleCarry={onToggleCarry}
						currentTeacherId={currentTeacherId}
						{...arrive}
					/>
				</div>
			)}

			{/* NO TRACK STACK HERE SINCE PASS 3 E2 (2026-09-28). The pills,
			    transport, cut words, role marks, job lines, Add a track and Tracks
			    on file used to sit above the note list, eleven blocks deep on a
			    phone. Each has a home now and nothing was dropped: the live half
			    (play, seek, Track key, Save key, Second screen, jobs, the cut
			    pills) is the track strip over the notes (tracks/TrackStrip.js);
			    the catalogue, the marks, Add a track and Tracks on file are the
			    Song panel's Tracks mode (songpanel/TracksPane.js), reached from
			    the strip's All tracks, the tool row below 1200, and the column at
			    1200+; Tracks on file is also a labelled section of the Student
			    drawer, so it stays one tap away from Warm-ups. */}

			{/* The warmup player, below 1200px only (the right column takes it on
			    desktop). Above the notes so the jot bar stays where it is; compact
			    so the phone tab body is not the whole screen of transport. */}
			{currentIsWarmups && warmups && warmups.mount === 'notes' && (
				// `routine` when the strip already carries the deck (phone, I1 phase
				// 3c) — otherwise the transport would render twice on one screen over
				// one engine, which is the hazard this whole split exists to avoid.
				<LessonWarmups warmups={warmups} region={deckInStrip ? 'routine' : 'full'} />
			)}

			{/* Tool row (pass 3 D2): below 1200 the Song panel is a sheet, and
			    these three open it on that mode. At 1200+ the panel is the right
			    column and this row is hidden in CSS. Videos is here now too: the
			    slots, Change and the picker work at every width; whether the
			    stage popout itself suits an iPad is release 5's question (card
			    "Video popout on phone/iPad"), not a reason to hide the videos. */}
			{!currentIsWarmups && onOpenPanel && (
				<div className="ld-toolrow" role="group" aria-label="Song panel">
					<button type="button" className="ld-tool ld-tool--panel" onClick={() => onOpenPanel('lyrics')}>
						<i className="fa-sharp fa-solid fa-align-left" aria-hidden="true" />
						Lyrics
					</button>
					<button type="button" className="ld-tool ld-tool--panel" onClick={() => onOpenPanel('tracks')}>
						<i className="fa-sharp fa-solid fa-list-music" aria-hidden="true" />
						Tracks{panelCounts.tracks ? ` ${panelCounts.tracks}` : ''}
					</button>
					<button type="button" className="ld-tool ld-tool--panel" onClick={() => onOpenPanel('videos')}>
						<i className="fa-brands fa-youtube" aria-hidden="true" />
						Videos{panelCounts.videos ? ` ${panelCounts.videos}` : ''}
					</button>
				</div>
			)}

			{isFirstLesson ? (
				<div className="ld-firstlesson">
					<i className="fa-sharp fa-solid fa-music ld-firstlesson__icon" aria-hidden="true" />
					<h2 className="ld-firstlesson__title">
						{student?.firstName || student?.fullName?.split(' ')[0] || 'This'}
						{"'s first lesson"}
					</h2>
					<p className="ld-firstlesson__body">
						No carry-forward yet — that's right. Add the song they're working on,
						or just start jotting on Warm-ups.
					</p>
					<button type="button" className="ld-firstlesson__cta" data-guide="lesson-add-song" onClick={onAddSong}>
						<i className="fa-sharp fa-solid fa-magnifying-glass" aria-hidden="true" />
						Find a song
					</button>
				</div>
			) : (
				<NoteList
					openNotes={notes.openNotes}
					landedNotes={notes.landedNotes}
					teacherNames={teacherNames}
					currentTeacherId={currentTeacherId}
					onDeliver={onDeliver}
					// Admin / owner only (2026-09-27); NoteRow renders no pencil or
					// trash without a handler, and the rules refuse the write anyway.
					onEdit={canManageNotes ? notes.updateNoteText : null}
					onDelete={canManageNotes ? notes.deleteNote : null}
					needsDeleteConfirm={notes.needsDeleteConfirm}
					sections={sections}
					onSetSection={notes.setNoteSection}
					emptyLabel={
						currentIsWarmups
							? 'Warm-ups — jot anything here; it stays yours until you deliver it.'
							: 'No notes yet — jot the first one below.'
					}
				/>
			)}
		</div>
	);
};

export default NotesColumn;
